import { Link, router } from "@inertiajs/react"
import { Trash2, TrendingUp } from "lucide-react"

import { Heading } from "@/components/heading"
import { PlayerStatusBadge } from "@/components/player-status-badge"
import { SkillBadge } from "@/components/skill-badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { formatGold, withPlus } from "@/lib/format"
import { leagueTeamPlayerPath } from "@/routes"
import type { TeamPlayer } from "@/types"

export function RosterTable({
  players,
  teamValue,
  leagueId,
  teamId,
  isOwner,
  canFire,
  onFireClick,
}: {
  players: TeamPlayer[]
  teamValue: number
  leagueId: string
  teamId: string
  isOwner: boolean
  canFire: boolean
  onFireClick: (player: TeamPlayer) => void
}) {
  return (
    <div className="mb-10">
      <Card className="py-0">
        <CardContent className="pt-5 pb-4">
          <Heading level={2} wrapperClassName="mb-4">
            Roster
          </Heading>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10 text-center">#</TableHead>
                <TableHead>Player</TableHead>
                <TableHead className="hidden sm:table-cell">Position</TableHead>
                <TableHead className="w-10 text-center text-[10px] font-bold tracking-wider text-[var(--accent-dim)] uppercase">
                  MA
                </TableHead>
                <TableHead className="w-10 text-center text-[10px] font-bold tracking-wider text-[var(--accent-dim)] uppercase">
                  ST
                </TableHead>
                <TableHead className="w-10 text-center text-[10px] font-bold tracking-wider text-[var(--accent-dim)] uppercase">
                  AG
                </TableHead>
                <TableHead className="w-10 text-center text-[10px] font-bold tracking-wider text-[var(--accent-dim)] uppercase">
                  PA
                </TableHead>
                <TableHead className="w-10 text-center text-[10px] font-bold tracking-wider text-[var(--accent-dim)] uppercase">
                  AV
                </TableHead>
                <TableHead className="hidden lg:table-cell">Skills</TableHead>
                <TableHead className="text-right">Value</TableHead>
                {isOwner && <TableHead className="w-10" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {players.map((player) => {
                const fireDisabled = !canFire || player.status === "dead"
                const fireTooltip =
                  player.status === "dead"
                    ? "Cannot fire a dead player"
                    : !canFire
                      ? "Cannot fire — team must have at least 11 players"
                      : undefined

                return (
                  <TableRow
                    key={player.id}
                    className="cursor-pointer"
                    style={
                      player.pending_level_up
                        ? {
                            background: "oklch(0.20 0.04 85 / 0.2)",
                          }
                        : undefined
                    }
                    onClick={() => {
                      void router.visit(
                        leagueTeamPlayerPath(leagueId, teamId, player.id),
                      )
                    }}
                  >
                    <TableCell className="text-muted-foreground text-center text-sm">
                      {player.number}
                    </TableCell>
                    <TableCell className="font-semibold">
                      <div className="flex items-center gap-2">
                        <Link
                          href={leagueTeamPlayerPath(
                            leagueId,
                            teamId,
                            player.id,
                          )}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {player.name}
                        </Link>
                        <PlayerStatusBadge
                          status={player.status}
                          injuryDescription={player.injury_description}
                        />
                        {player.pending_level_up && (
                          <span
                            className="inline-flex items-center gap-0.5 text-[10px] font-bold tracking-wider uppercase"
                            style={{ color: "oklch(0.65 0.20 145)" }}
                          >
                            <TrendingUp className="size-3" />
                            Level Up
                            {player.pending_level_up_count &&
                            player.pending_level_up_count > 1
                              ? ` ×${player.pending_level_up_count}`
                              : ""}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground hidden sm:table-cell">
                      {player.position}
                    </TableCell>
                    <TableCell className="text-center">{player.ma}</TableCell>
                    <TableCell className="text-center">{player.st}</TableCell>
                    <TableCell className="text-center">
                      {withPlus(player.ag)}
                    </TableCell>
                    <TableCell className="text-center">
                      {withPlus(player.pa)}
                    </TableCell>
                    <TableCell className="text-center">
                      {withPlus(player.av)}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <div className="flex flex-wrap gap-1">
                        {player.skills.map((skill) => (
                          <SkillBadge key={skill} name={skill} />
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-semibold">
                      {formatGold(player.cost)}
                    </TableCell>
                    {isOwner && (
                      <TableCell
                        className="w-10 text-center"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {fireTooltip ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  disabled={fireDisabled}
                                  className="text-muted-foreground h-7 w-7"
                                  aria-label={`Fire ${player.name}`}
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{fireTooltip}</p>
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          <Button
                            variant="ghost"
                            size="icon"
                            disabled={fireDisabled}
                            className="text-muted-foreground hover:text-destructive h-7 w-7"
                            aria-label={`Fire ${player.name}`}
                            onClick={() => onFireClick(player)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                )
              })}
            </TableBody>
            <TableFooter>
              <TableRow className="border-t-2 border-[var(--border-heavy)]">
                <TableCell
                  colSpan={isOwner ? 10 : 9}
                  className="font-display text-lg leading-none font-bold"
                  style={{
                    color: "var(--accent)",
                    textShadow:
                      "0 0 4px oklch(0.8 0.16 85 / 0.25), 0 0 16px oklch(0.8 0.3 145 / 0.12), 0 1px 0 oklch(0.55 0.1 85), 0 1px 3px oklch(0 0 0 / 0.4)",
                  }}
                >
                  Total Team Value
                </TableCell>
                <TableCell className="font-display text-right text-lg leading-none font-bold">
                  {formatGold(teamValue)} gp
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </CardContent>
      </Card>
    </div>
  )
}
